import {ChangeDetectionStrategy, Component, inject, Input, OnInit, Type} from '@angular/core';
import {NgComponentOutlet} from '@angular/common';
import {createSelector, Store} from '@ngrx/store';
import {
  ColumnDefinition,
  ComponentPrintBehaviorDefinition,
  LoadableSelectorContainer,
  PrintBehaviorDefinition,
  TableDefinition
} from '@aviary/components/table/table.component.types';
import {
  generateColumnHeaderFrom,
  generateColumnNameFrom,
  getSelectorCreatorFrom,
  resolveCellComponentProperties
} from '@aviary/components/table/table.component.helpers';
import {PrintableTableDefinition} from '@aviary/components/table-card/table-card.component.types';
import {
  FilterOptionsSelector,
  isSelectFilterDefinition,
  TableCardHeaderFilterDefinitions
} from '@aviary/components/table-card/table-card-header-filters/table-card-header-filter.types';
import {MxSelector, MxSelectorCreator} from '@aviary/store/store.types';
import {snapshot} from '@aviary/store/store.helpers';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {RouterSelectors} from '@aviary/store/router/router.selectors';
import {PaginationSelectors} from '@aviary/store/pagination/pagination.selectors';
import {capitalize, kebabCaseToSentenceCase} from '@aviary/store/transformation.helpers';
import {isArray, isDefined, isPopulatedString, isString} from '@aviary/store/common/typing.helpers';
import {now} from '@aviary/store/common/date.helpers';
import {SelectOptions} from '@aviary/store/common/common.types';
import {StatusChipComponent} from '@aviary/components/status-chip/status-chip.component';

interface PrintHeaderFilter {
  label: string;
  values: string;
}

interface PrintHeader {
  label: string;
  widthCssClasses: string;
}

interface PrintTextCell {
  isComponent: false;
  text: string;

  // These are from PrintComponentCell and don't really belong here.  It's a trick to stop IntelliJ from erroneously reporting a typing issue in the template.
  componentClass?: never;
  componentInputs?: never;
  overrideScreenTypography?: never;
}

interface PrintComponentCell {
  isComponent: true;
  componentClass: Type<unknown>;
  componentInputs: Record<string, unknown>;
  overrideScreenTypography: boolean;

  // This is from PrintTextCell and doesn't really belong here.  It's a trick to stop IntelliJ from erroneously reporting a typing issue in the template.
  text?: never;
}

type PrintCell = PrintTextCell | PrintComponentCell;

/**
 * A minimal, print-only rendering of a table definition: plain semantic table, current page of data, no interactive elements.  Intended to be rendered
 * exclusively through `PrintService.print(PrintableTableComponent, {...})`, which sets the inputs before triggering change detection.
 *
 * NOTE: This component must not declare component-scoped styles.  `PrintService` copies document styles into its iframe before this component renders,
 * so late-injected component styles would never arrive.  Style with Tailwind utility classes and the global `.iframe-print-content` block.
 */
@Component({
  selector: 'mx-printable-table',
  templateUrl: './printable-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet]
})
export class PrintableTableComponent<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer> implements OnInit {

  @Input({required: true}) tableDefinition: TableDefinition<ObjectType, DefaultSelectorContainer>;
  @Input({required: true}) printDefinition: PrintableTableDefinition;
  @Input() filterDefinitions: TableCardHeaderFilterDefinitions = [];

  public title: string = '';
  public printedAt: string = '';
  public filterSummary: PrintHeaderFilter[] = [];
  public rowCountSummary: string = '';
  public headers: PrintHeader[] = [];
  public rows: PrintCell[][] = [];

  private readonly store: Store = inject(Store);
  private readonly formatPipe: FormatPipe = inject(FormatPipe);

  public ngOnInit(): void {
    const container: DefaultSelectorContainer = this.tableDefinition.defaultSelectorContainer;

    this.title = isString(this.printDefinition.title) ? this.printDefinition.title : this.snapshot(this.printDefinition.title) ?? '';
    this.printedAt = this.formatPipe.transform(now(), 'DATE_TIME');
    this.filterSummary = this.buildFilterSummary();

    const includedColumns: ColumnDefinition<ObjectType, DefaultSelectorContainer>[] =
      this.tableDefinition.columns.filter(column => !column.whenPrinting?.omit);

    this.headers = includedColumns.map((column, columnIndex) => ({
      label: generateColumnHeaderFrom(column, generateColumnNameFrom(column, columnIndex), columnIndex),
      widthCssClasses: column.whenPrinting?.headerCssClasses ?? extractWidthCssClassesFrom(column.headerCssClasses)
    }));

    const dataSourceSelector: MxSelector<ObjectType[]> =
      isString(this.tableDefinition.dataSource)
        ? container[String(this.tableDefinition.dataSource)]
        : this.tableDefinition.dataSource;

    const rowObjects: ObjectType[] = this.snapshot(dataSourceSelector) ?? [];

    this.rows = rowObjects.map(rowObject => includedColumns.map(column => this.buildCellFor(rowObject, column, container)));

    this.rowCountSummary = this.buildRowCountSummary();
  }

  private snapshot<T>(selector: MxSelector<T>): T | undefined {
    return snapshot(selector, this.store);
  }

  private buildCellFor(
    rowObject: ObjectType, column: ColumnDefinition<ObjectType, DefaultSelectorContainer>, container: DefaultSelectorContainer
  ): PrintCell {
    if (column.type === 'component') {
      const whenPrinting: ComponentPrintBehaviorDefinition<ObjectType, DefaultSelectorContainer> | undefined = column.whenPrinting;

      const overrideValueSelectorCreator: MxSelectorCreator<[ObjectType], string> | undefined =
        getSelectorCreatorFrom(container, whenPrinting?.overrideValue);

      return isDefined(overrideValueSelectorCreator)
        ? {
          isComponent: false,
          text: String(this.snapshot(overrideValueSelectorCreator(rowObject)) ?? '')
        }
        : {
          isComponent: true,
          componentClass: column.componentClass,
          componentInputs: resolveCellComponentProperties(column.componentProperties ?? {}, rowObject, container, selector => this.snapshot(selector)),
          overrideScreenTypography: !whenPrinting?.preserveScreenTypography // Override typography inside the component unless told to preserve it.
        };
    }

    const whenPrinting: PrintBehaviorDefinition<ObjectType, DefaultSelectorContainer> | undefined = column.whenPrinting;
    const valueSelectorCreator = getSelectorCreatorFrom(container, whenPrinting?.overrideValue ?? column.value);
    const rawValue: unknown = isDefined(valueSelectorCreator) ? this.snapshot(valueSelectorCreator(rowObject)) : undefined;

    if (column.type === 'statusChip') {
      // Use StatusChipComponent only to get the correct label.  Style overrides will remove the chip wrapper and leave only the label text.
      return {
        isComponent: true,
        componentClass: StatusChipComponent,
        componentInputs: {
          status: rawValue,
          colors: column.statusChipColors,
          displayableStatusNames: column.displayableStatusNames
        },
        overrideScreenTypography: true
      }
    }

    return {isComponent: false, text: this.formatPipe.transform(rawValue, column.format ?? 'NONE')};
  }

  private buildFilterSummary(): PrintHeaderFilter[] {
    const summaries: PrintHeaderFilter[] = [];

    // Standard search boxes write the 'query' param (hk-search pages) or the 'search' param (table-card search boxes).
    const searchTerm: string | undefined = this.snapshot(RouterSelectors.queryParamValue('query')) ?? this.snapshot(RouterSelectors.queryParamValue('search'));

    if (isPopulatedString(searchTerm)) summaries.push({label: 'Search', values: searchTerm});

    for (const definition of this.filterDefinitions) {
      const paramValue: string | undefined = this.snapshot(RouterSelectors.queryParamValue(definition.queryParamName));
      if (!isPopulatedString(paramValue)) continue;

      const label: string = capitalize(kebabCaseToSentenceCase(definition.queryParamName));

      if (isSelectFilterDefinition(definition)) {
        // Translate the raw query param values into the same display names the filter buttons show.
        const optionsSelector: MxSelector<SelectOptions<string>> =
          isArray(definition.filterValues)
            ? createSelector(
              () => (definition.filterValues as string[]).map(value => ({displayName: this.formatPipe.transform(value, 'TITLE_CASE'), value}))
            )
            : createSelector(
              definition.filterValues as FilterOptionsSelector,
              options => options.map(({displayName, value}) => ({displayName, value}))
            );

        const selectedOptions: SelectOptions<string> =
          this.snapshot(RouterSelectors.queryParamValueAsSelectOptions(definition.queryParamName, optionsSelector)) ?? [];

        const values: string =
          selectedOptions.length > 0
            ? selectedOptions.map(option => option.displayName).join(', ')
            : paramValue.split(',').map(decodeURIComponent).join(', ');

        summaries.push({label, values});
      } else if (definition.type === 'dateRange') {
        summaries.push({label, values: this.formatPipe.transform(paramValue, 'DATE_ONLY_RANGE')});
      } else {
        summaries.push({label, values: paramValue.split(',').map(decodeURIComponent).join(', ')});
      }
    }

    return summaries;
  }

  private buildRowCountSummary(): string {
    const shownCount: number = this.rows.length;
    const usesPagination: boolean = this.tableDefinition.includePaginationControls ?? true;
    const totalCount: number = usesPagination ? this.snapshot(PaginationSelectors.totalCount) ?? shownCount : shownCount;

    return shownCount < totalCount
      ? `Showing ${shownCount} of ${totalCount} items`
      : `${shownCount} ${shownCount === 1 ? 'item' : 'items'}`;
  }
}

/** The print table must use fixed layout so it can never exceed the page width (auto layout overflows the page when column minimum-content demands exceed
 *  it — e.g. Material chips carry their own nowrap).  Mirror only the width tokens from each column's headerCssClasses onto the print header cells,
 *  so that any non-width header styling stays out of the printable view. */
function extractWidthCssClassesFrom(headerCssClasses?: string): string {
  return (headerCssClasses ?? '')
    .split(/\s+/)
    .filter(cssClass => /^(w-|min-w-|max-w-)/.test(cssClass))
    .join(' ');
}
